import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadJobs, saveJobs } from './jobsStorage';
import JobStatusPoller from './JobStatusPoller';

// Global, app-level registry of in-flight Lambda report renders (full report +
// the heavy Production Processes section). Lifts job tracking OUT of the report
// page so the banner — and the polling — survive navigation across the whole
// operator app. The report page registers a job when generation starts; the
// renderless pollers below keep render-status alive from then on.
//
// A "job" descriptor:
//   { key, reportId, diagramId, slot, recordKey, inputHash, type, name,
//     subtitle, registeredAt }  (+ poller-filled: status, startedAt,
//     finishedAt, url, message)
// `key` === `${recordKey}|${inputHash}` (the same identity useLambdaPdf uses).

const BackgroundJobsContext = createContext(null);

export function useBackgroundJobs() {
  // Returns null when no provider is mounted (e.g. signed-out tree) — callers
  // guard with optional chaining so report hooks never crash outside the app.
  return useContext(BackgroundJobsContext);
}

const FINISHED_STATES = new Set(['ready', 'error', 'expired']);
// Grace window after registration before a transient absent/expired poll is
// treated as "render gone" — avoids dropping a job in the brief gap between the
// POST /generate and the backend claiming the slot.
const ABSENT_GRACE_MS = 8000;

export function BackgroundJobsProvider({ orgId, children }) {
  // Storage is org-scoped. The provider is also keyed by org id at the mount
  // site (_app.jsx), so on an org switch it fully remounts — this initializer
  // re-seeds from the NEW org's storage and old pollers tear down. `orgId` is
  // therefore stable for the provider's lifetime.
  const [jobs, setJobs] = useState(() => loadJobs(orgId));

  // Keep the CSS spacer var defined app-wide so layout calc()s always resolve,
  // even before the banner mounts. The banner toggles it to the bar height.
  useEffect(() => {
    document.documentElement.style.setProperty('--cg-jobs-banner-h', '0px');
  }, []);

  useEffect(() => {
    saveJobs(orgId, jobs);
  }, [orgId, jobs]);

  const registerJob = useCallback((descriptor) => {
    setJobs((prev) => {
      // A new render for the same (report, slot) but a different content hash
      // supersedes any still-pending render of the old content.
      const pruned = prev.filter(
        (j) =>
          !(
            j.reportId === descriptor.reportId &&
            j.slot === descriptor.slot &&
            j.inputHash !== descriptor.inputHash &&
            j.status === 'pending'
          ),
      );
      const existing = pruned.find((j) => j.key === descriptor.key);
      if (existing) {
        // Refresh display metadata but preserve live status + the original
        // registeredAt (so the absent-grace timer isn't reset on re-register).
        return pruned.map((j) =>
          j.key === descriptor.key
            ? { ...j, ...descriptor, status: j.status, registeredAt: j.registeredAt }
            : j,
        );
      }
      return [
        ...pruned,
        {
          status: 'pending',
          startedAt: null,
          finishedAt: null,
          url: null,
          message: null,
          ...descriptor,
        },
      ];
    });
  }, []);

  const updateJobStatus = useCallback((key, data) => {
    setJobs((prev) => {
      let changed = false;
      const next = prev.flatMap((j) => {
        if (j.key !== key) return [j];

        if (data.status === 'absent' || data.status === 'expired') {
          // Transient right after a POST — ignore within the grace window.
          if (Date.now() - (j.registeredAt || 0) < ABSENT_GRACE_MS) return [j];
          changed = true;
          return []; // render is gone from S3 → drop the job
        }

        const merged = {
          ...j,
          status: data.status ?? j.status,
          startedAt: data.startedAt ?? j.startedAt,
          finishedAt: data.finishedAt ?? j.finishedAt,
          url: data.url ?? j.url,
          message: data.message ?? j.message,
        };
        if (
          merged.status === j.status &&
          merged.startedAt === j.startedAt &&
          merged.finishedAt === j.finishedAt &&
          merged.url === j.url &&
          merged.message === j.message
        ) {
          return [j]; // no-op → keep the same reference (avoids a re-render)
        }
        changed = true;
        return [merged];
      });
      return changed ? next : prev;
    });
  }, []);

  // A full report's file actually downloaded → clear it (per the confirmed UX:
  // ready jobs persist until downloaded, not merely until ready).
  const markDownloaded = useCallback((key) => {
    setJobs((prev) => prev.filter((j) => j.key !== key));
  }, []);

  const removeJob = useCallback((key) => {
    setJobs((prev) => prev.filter((j) => j.key !== key));
  }, []);

  const dismissAllFinished = useCallback(() => {
    setJobs((prev) => prev.filter((j) => !FINISHED_STATES.has(j.status)));
  }, []);

  const value = useMemo(
    () => ({ jobs, registerJob, updateJobStatus, markDownloaded, removeJob, dismissAllFinished }),
    [jobs, registerJob, updateJobStatus, markDownloaded, removeJob, dismissAllFinished],
  );

  return (
    <BackgroundJobsContext.Provider value={value}>
      {jobs.map((job) => (
        <JobStatusPoller
          key={job.key}
          job={job}
          onStatus={updateJobStatus}
        />
      ))}
      {children}
    </BackgroundJobsContext.Provider>
  );
}

export default BackgroundJobsContext;
